import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import { css } from '@emotion/css';
import { withAlpha } from '../theme/color';
import meridianOrangePalette from '../theme/meridian-orange';
import { createTransition, reducedMotionQuery, shimmerKeyframes, skeletonReveal } from '../theme/motion';

const SURFACE_BORDER_RADIUS = 12;

const surfaceShadows = {
  light: {
    resting: '0 1px 2px rgba(2, 8, 14, 0.04), 0 1px 1px rgba(2, 8, 14, 0.02)',
    raised: '0 2px 4px rgba(2, 8, 14, 0.04), 0 12px 24px -8px rgba(2, 8, 14, 0.12)',
  },
  dark: {
    resting: '0 1px 2px rgba(0, 0, 0, 0.3)',
    raised: '0 2px 4px rgba(0, 0, 0, 0.3), 0 12px 24px -8px rgba(0, 0, 0, 0.5)',
  },
} as const;

export default function useSurfaceStyles() {
  const meridianTheme = useMeridianTheme();
  const isDarkMode = meridianTheme.mode === 'dark';
  const shadows = isDarkMode ? surfaceShadows.dark : surfaceShadows.light;

  const surfaceClass = css({
    borderRadius: SURFACE_BORDER_RADIUS,
    border: `1px solid ${meridianTheme('themeBorderSubdued')}`,
    backgroundColor: meridianTheme('themeSurfaceDefault'),
    boxShadow: shadows.resting,
  });

  const interactiveSurfaceClass = css(surfaceClass, {
    cursor: 'pointer',
    transition: createTransition(['border-color', 'box-shadow', 'transform']),
    '&:hover': {
      borderColor: meridianTheme('themeBorderSurface'),
      boxShadow: shadows.raised,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: shadows.resting,
    },
    '&:focus-visible': {
      outline: `2px solid ${meridianTheme('themeBorderFocus')}`,
      outlineOffset: 2,
    },
    [reducedMotionQuery]: {
      transition: 'none',
      '&:hover': { transform: 'none' },
    },
  });

  const skeletonClass = css({
    borderRadius: 6,
    backgroundColor: meridianTheme('themeBorderSubdued'),
    backgroundImage: `linear-gradient(90deg, transparent 0%, ${meridianTheme('themeSurfaceDefault')} 50%, transparent 100%)`,
    backgroundSize: '200% 100%',
    animation: `${skeletonReveal}, ${shimmerKeyframes} 1.4s ease-in-out infinite`,
    [reducedMotionQuery]: { animation: skeletonReveal },
  });

  const skeletonSurfaceClass = css(surfaceClass, {
    animation: skeletonReveal,
  });

  const accentIconClass = css({
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 10,
    background: `linear-gradient(140deg, ${meridianOrangePalette.palette300} 0%, ${meridianOrangePalette.palette500} 100%)`,
    boxShadow: `0 4px 12px -2px ${withAlpha(meridianOrangePalette.palette500, 0.35)}`,
    color: '#ffffff',
  });

  const accentTintColor = isDarkMode
    ? withAlpha(meridianOrangePalette.palette300, 0.16)
    : meridianOrangePalette.palette50;
  const accentRingColor = withAlpha(meridianOrangePalette.palette300, isDarkMode ? 0.28 : 0.22);

  // Shared text field chrome. :focus-within also matches the element itself, so this works both on a
  // bare <input> and on a wrapper that contains the input alongside buttons.
  const fieldClass = css({
    boxSizing: 'border-box',
    border: `1px solid ${meridianTheme('themeBorderSubdued')}`,
    backgroundColor: meridianTheme('themeSurfaceDefault'),
    color: meridianTheme('themeInkDefault'),
    fontFamily: 'inherit',
    outline: 'none',
    transition: createTransition(['border-color', 'box-shadow']),
    '&:hover': { borderColor: meridianTheme('themeBorderSurface') },
    '&:focus-within': {
      borderColor: meridianTheme('themeActionPrimaryDefault'),
      boxShadow: `0 0 0 3px ${accentRingColor}`,
    },
    '&::placeholder, & ::placeholder': { color: meridianTheme('themeInkSubdued') },
    [reducedMotionQuery]: { transition: 'none' },
  });

  const accentPillClass = css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 20,
    padding: '0 8px',
    borderRadius: 999,
    border: `1px solid ${withAlpha(meridianOrangePalette.palette300, isDarkMode ? 0.32 : 0.28)}`,
    backgroundColor: accentTintColor,
    color: isDarkMode ? meridianOrangePalette.palette200 : meridianOrangePalette.palette600,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    lineHeight: 1,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    '&::before': {
      content: '""',
      width: 5,
      height: 5,
      borderRadius: '50%',
      backgroundColor: meridianOrangePalette.palette300,
    },
  });

  return {
    surfaceClass,
    interactiveSurfaceClass,
    skeletonClass,
    skeletonSurfaceClass,
    accentIconClass,
    accentPillClass,
    fieldClass,
    accentTintColor,
    accentRingColor,
  };
}
